/**
 * PermissionMatrix — the grouped checkbox grid that drives the role editor
 * (ADR-0030). Reads the canonical catalog from `@flint/api-contract` so it can
 * never drift from what the server enforces. Read-only mode renders the same
 * grid with disabled inputs (for viewing a system role).
 */
import { PERMISSION_GROUPS, PERMISSION_AREAS } from "@flint/api-contract";
import { AREA_LABELS, permissionLabel } from "./lib/format.ts";

export interface PermissionMatrixProps {
  /** Currently-granted permission strings. */
  value: ReadonlySet<string>;
  /** Toggle a single permission. Omitted in read-only mode. */
  onToggle?: (permission: string, next: boolean) => void;
  /** Select / clear a whole area at once — receives that area's permission list. */
  onToggleArea?: (permissions: readonly string[], next: boolean) => void;
  readOnly?: boolean;
}

export function PermissionMatrix({ value, onToggle, onToggleArea, readOnly }: PermissionMatrixProps) {
  return (
    <div className="flex flex-col gap-4">
      {PERMISSION_AREAS.map((area) => {
        const perms = PERMISSION_GROUPS[area];
        const granted = perms.filter((p) => value.has(p)).length;
        const allOn = granted === perms.length;
        return (
          <section key={area} className="rounded-md border border-line bg-paper">
            <header className="flex items-center justify-between gap-2 border-b border-line bg-raised px-3.5 py-2">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-ink">{AREA_LABELS[area] ?? area}</h4>
                <span className="font-mono text-xs text-muted">
                  {granted}/{perms.length}
                </span>
              </div>
              {!readOnly && onToggleArea ? (
                <button
                  type="button"
                  onClick={() => onToggleArea(perms, !allOn)}
                  className="text-xs font-medium text-sky-700 hover:underline"
                >
                  {allOn ? "Clear all" : "Select all"}
                </button>
              ) : null}
            </header>
            <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 p-3 sm:grid-cols-2 xl:grid-cols-3">
              {perms.map((p) => {
                const checked = value.has(p);
                return (
                  <label
                    key={p}
                    className={`flex items-center gap-2 text-sm ${
                      readOnly ? "text-ink-2" : "cursor-pointer text-ink-2 hover:text-ink"
                    }`}
                    title={p}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={readOnly}
                      onChange={(e) => onToggle?.(p, e.currentTarget.checked)}
                      className="h-3.5 w-3.5 shrink-0 accent-sky-600 disabled:opacity-60"
                    />
                    <span className="truncate">{permissionLabel(p)}</span>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
